import { Tokens } from "@Utilities/enum";
import { computed, ref, reactive, watch, onMounted } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import { DocumentIcon } from "@heroicons/vue/outline";
import _ from "lodash";

import useWindowResize from "@Functions/useWindowResize.js";

export default function () {
    const store = useStore();
    const router = useRouter();
    const currentSidebar = ref();
    const showModal = ref(false);
    const sectionLevelSelected = ref(0);
    const subscriptionDetails = ref({});
    const saveSections = ref();
    const userMetricLevel = window.Subscription.highest_subscription.metric_level
    const accessAllNav = userMetricLevel >= 1
    let section_ids = [];

    let sectionSelected = reactive({
        level1: {},
        level2: {},
        level3: {},
    });

    let tempSectionSelected = reactive({
        level1: {},
        level2: {},
        level3: {},
    });

    let metricSelected = reactive({
        item: {}
    })

    let arr = ref([ 1 ])

    onMounted(async () => {
        const menuNav = await store.dispatch("Cookies/onGetCookies", Tokens.MenuNav)
        saveSections.value = !_.isEmpty(menuNav) ? menuNav.item : ''
    })

    const { isTablet } = useWindowResize();

    const showBackButton = computed(() => {
        return sectionLevelSelected.value > 0 && isTablet.value
    })

    const isLoading = computed(() => {
        return store.getters["Analytics/isLoading"];
    });

    const { highest_subscription, access, subscription_types } =
        window.Subscription;

    const onSetCurrentSidebar = (val) => {
        currentSidebar.value = val;
    };

    const emptyStateProps = computed(() => {
        return {
            icon: DocumentIcon,
            subtitle: "Select a therapeutic class",
            class: "",
        };
    })

    watch(
        () => router.currentRoute.value,
        () => {
            sectionLevelSelected.value = 0
        }
    );

    const setDefaultSection = () => {
        const routeParams = router.currentRoute.value.params;
        const routeQuery = router.currentRoute.value.query;

        if ([ 'indication', 'specialty' ].includes(routeParams.type) || routeQuery.sort === 'brand' || routeQuery.sort === 'generic') { // if metric is tc
            return
        }
        arr.value = [ 1, 2, 3 ]
        sectionLevelSelected.value = 1;

        if (_.isEmpty(saveSections.value.level3)) {
            arr.value = [ 1, 2 ]
            if (_.isEmpty(saveSections.value.level2)) {
                arr.value = [ 1 ]
            }
        }

        sectionSelected.level1 = saveSections.value.level1 ?? {};
        sectionSelected.level2 = saveSections.value.level2 ?? {};
        sectionSelected.slug = saveSections.value.slug ?? {};
        sectionSelected.type = saveSections.value.type ?? {};

        if (!sectionSelected.level1.children) {
            sectionSelected.level2 = {}
            sectionSelected.level3 = {}
        }

        if (!_.isEmpty(saveSections.value.level3)) {
            sectionSelected.level3 = saveSections.value.level3;
        }
        // to remove undefined field
        _.forEach(Object.keys(sectionSelected), key => {
            if (key === 'undefined') {
                delete sectionSelected[key];
            }
        })

        store.dispatch('Cookies/onSetCookies', {
            key: Tokens.MenuNav, value: {
                current: 'Therapeutic Class',
                type: '',
                item: sectionSelected
            }
        })

    };

    const sectionId = ref(null)
    const onShowModal = () => {
        if (!_.isEmpty(saveSections.value) && !_.isEmpty(saveSections.value.level2)) {
            sectionId.value = saveSections.value.level2.id
        }
        setDefaultSection();
        tempSectionSelected = {};
        showModal.value = true;

        if (isTablet.value) {
            if ( sectionLevelSelected.value === 1 ) {
                --sectionLevelSelected.value
            }
        }
    };

    const updateSectionId = (id) => {
        sectionId.value = id
    }

    const onSectionClicked = (section, section_level) => {
        setSectionValue(section_level, section, true);
        sectionLevelSelected.value = section_level;
    };

    const setSectionValue = (section_level, data, temp = false) => {
        arr.value = [ 1, 2, 3 ]

        if (section_level === 1) {
            arr.value = [ 1, 2 ]
            sectionSelected.level1 = data;
            if (temp) {
                tempSectionSelected.level1 = data;
            }

            if (!data.children) {
                sectionSelected.level2 = {};
                tempSectionSelected.level2 = {};
                sectionSelected.level3 = {};
                tempSectionSelected.level3 = {};
            }

            section_ids = [];
        }

        if (section_level === 2) {
            arr.value = [ 1, 2, 3 ]
            if (!data.viewSubclass) {
                // to avoid showing level3 subclass list
                arr.value = [ 1, 2 ]
            }
            sectionSelected.level2 = data;
            if (temp) {
                tempSectionSelected.level2 = data;
            }

            if (!data.children) {
                sectionSelected.level3 = {};
                tempSectionSelected.level3 = {};
            }

            section_ids.splice(3, 1);
        }

        if (section_level === 3) {
            sectionSelected.level3 = data;
            if (temp) {
                tempSectionSelected.level3 = data;
            }
        }

        subscriptionDetails.value = null;
        section_ids[section_level] = data.id;

        const sectionSubscriptions = _.orderBy(
            _.filter(subscription_types, (subscription) => {
                return _.includes(section_ids, subscription.section_id);
            }),
            [ "metric_level" ],
            [ "desc" ]
        );

        subscriptionDetails.value = _.head(sectionSubscriptions);
    };

    const onSetSections = (level) => {
        let metric = "main-class";
        let slug = null;

        if (!_.isEmpty(tempSectionSelected.level1)) {
            sectionSelected = {};
            sectionSelected.level1 = tempSectionSelected.level1;
        }

        if (!_.isEmpty(tempSectionSelected.level2)) {
            sectionSelected.level2 = tempSectionSelected.level2;
            sectionSelected.level3 = {};
        }

        if (!_.isEmpty(tempSectionSelected.level3)) {
            sectionSelected.level3 = tempSectionSelected.level3;
        }

        _.forEach(Object.keys(sectionSelected), key => {
            if (key === 'undefined') {
                delete sectionSelected[key];
            }
        })

        if (level === 1) {
            sectionSelected.level2 = {};
            sectionSelected.level3 = {};
            slug = sectionSelected.level1.slug
        }

        if (level === 2) {
            metric = "classes"
            sectionSelected.level3 = {};
            slug = sectionSelected.level2.slug
        }

        if (level === 3) {
            metric = 'sub-class'
            slug = sectionSelected.level3.slug
        }

        sectionSelected.type = metric
        sectionSelected.slug = slug

        store.dispatch('Cookies/onSetCookies', {
            key: Tokens.MenuNav, value: {
                current: 'Therapeutic Class',
                type: '',
                item: sectionSelected
            }
        })

        showModal.value = false;

        let uri = `/report/drug-info/form/${metric}`;

        if (highest_subscription.metric_level < 5 && !_.isEmpty(subscriptionDetails.value) && !access.full_permutation_access) {
            const { dimension, attribute } =
                subscriptionDetails.value.dimension_metric;
            uri = `/report/${dimension}/${attribute}/${metric}/`;
        }

        if (slug) {
            uri = uri + `?slug=${slug}`;
        }

        window.location.href = uri;
    };

    const sectionProps = (sectionLevel) => {
        let parent_level = sectionLevel;
        if (sectionLevel > 1) {
            --parent_level;
        }

        const parent_section = sectionSelected[`level${parent_level}`];
        _.forEach(Object.keys(parent_section), key => {
            if (key === 'undefined') {
                delete parent_section[key];
            }
        })

        let parentIds = parent_section.id;

        if (sectionLevel === 1) {
            parentIds = _.join(access.main, ",");
        }

        const sectionIds = _.join(
            access[`section${sectionLevel}_access`],
            ","
        );

        const section = sectionSelected[`level${sectionLevel}`];
        _.forEach(Object.keys(section), key => {
            if (key === 'undefined') {
                delete section[key];
            }
        })

        const item = {
            sectionLevel,
            section,
            parentIds,
            sectionIds,
            metricLevel: getMetricLevel(parent_section.id, sectionId.value),
            clear: 0,
        };
        return item;
    };

    const goBackBtnProps = reactive({
        type: "white-btn",
        size: "xs",
        label: "Go back",
    })

    const getMetricLevel = (parent_section_id, section_id) => {
        if (highest_subscription.metric_level === 5) {
            return 5;
        }

        /* Checking if user can view section dashboard
            - Check first if user has access on section parent
            - Then check if user has direct access on section
        */

        const parentMetricLevel = getSectionMetricLevel(parent_section_id);
        const sectionLevel = getSectionMetricLevel(section_id);

        return parentMetricLevel ? parentMetricLevel : (sectionLevel ? sectionLevel : 0);
    }

    const getSectionMetricLevel = (section_id) => {
        const sectionLevel = _.find(subscription_types, (subscription) => {
            return section_id === subscription.section_id
        })

        return (!_.isEmpty(sectionLevel) || !_.isUndefined(sectionLevel)) ? sectionLevel.metric_level : null;
    }

    const showList = (index) => {
        let item = true

        if (isTablet.value) {
            item = sectionLevelSelected.value === index ||
                (sectionLevelSelected.value === 2 && index === 1 && _.size(arr.value) === 2)
        }

        return item
    }

    return {
        sectionSelected,
        metricSelected,
        sectionProps,
        onSectionClicked,
        onSetSections,
        isLoading,
        onSetCurrentSidebar,
        arr,
        sectionLevelSelected,
        emptyStateProps,
        showModal,
        setDefaultSection,
        onShowModal,
        sectionId,
        updateSectionId,
        showBackButton,
        showList,
        isTablet,
        goBackBtnProps,
        saveSections,
        highest_subscription,
        router,
        getSectionMetricLevel,
        store,
        userMetricLevel,
        accessAllNav
    };
}
